import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { MarketingConsentState, UpdateMarketingConsentData } from '../types/auth';

interface MarketingConsentEnvelope {
  status: 'success';
  data: MarketingConsentState;
}

/**
 * Grant (`true`) or withdraw (`false`) the signed-in person's marketing-email
 * consent. Every real change is recorded server-side as its own proof row;
 * asking for the state the person is already in is a harmless no-op.
 *
 * PUT /api/v1/auth/me/marketing-consent
 *
 * @returns The consent state after the request (the server's answer, which is
 *   what the UI should show).
 */
export const updateMarketingConsent = async (optIn: boolean): Promise<MarketingConsentState> => {
  const body: UpdateMarketingConsentData = { opt_in: optIn };
  const response = await apiClient.put<MarketingConsentEnvelope>(
    '/auth/me/marketing-consent',
    body
  );
  return response.data;
};

/**
 * Mutation hook around {@link updateMarketingConsent}; call `mutate(optIn)`.
 *
 * `onSuccess` / `onError` are passed as hook options (not per-`mutate` callbacks)
 * so they still fire if the caller unmounts mid-request — the server has changed
 * either way, and the session must not be left saying otherwise.
 */
export const useUpdateMarketingConsent = (
  options?: Pick<
    UseMutationOptions<MarketingConsentState, unknown, boolean>,
    'onSuccess' | 'onError'
  >
) =>
  useMutation<MarketingConsentState, unknown, boolean>({
    mutationFn: updateMarketingConsent,
    ...options,
  });
